import { atom, createStore, Getter, SetStateAction, Setter, useSetAtom } from 'jotai'
import { useEffect } from 'react'

export type Callback<Value> = (
	get: Getter,
	set: Setter,
	newVal: Value,
	prevVal: Value,
) => void

/**
 * Remember: this can miss events if the atom is updated before a listener is registered!
 */
export function atomWithListeners<Value>(initialValue: Value) {
	const baseAtom = atom(initialValue)
	const listenersAtom = atom<Callback<Value>[]>([])
	const anAtom = atom(
		(get) => get(baseAtom),
		(get, set, arg: SetStateAction<Value>) => {
			const prevVal = get(baseAtom)
			set(baseAtom, arg)
			const newVal = get(baseAtom)
			get(listenersAtom).forEach((callback) => {
				callback(get, set, newVal, prevVal)
			})
		},
	)
	const useListener = (callback: Callback<Value>, { store }: { store?: ReturnType<typeof createStore> } = {}) => {
		const setListeners = useSetAtom(listenersAtom, { store })
		useEffect(() => {
			setListeners((prev) => [...prev, callback])
			return () =>
				setListeners((prev) => {
					const index = prev.indexOf(callback)
					return [...prev.slice(0, index), ...prev.slice(index + 1)]
				})
		}, [setListeners, callback, store])
	}
	return [anAtom, useListener] as const
}
